import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "formulario-modelo" }
const _hoisted_2 = { class: "header-flex" }
const _hoisted_3 = {
  key: 0,
  class: "ctx-modelo"
}
const _hoisted_4 = {
  key: 0,
  class: "error"
}
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "filtro-botones" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = {
  key: 0,
  class: "mensaje-vacio"
}
const _hoisted_9 = {
  key: 1,
  class: "aspectos-container"
}
const _hoisted_10 = { class: "aspecto-header" }
const _hoisted_11 = { class: "aspecto-ordinal" }
const _hoisted_12 = { class: "aspecto-nombre" }
const _hoisted_13 = { class: "indicadores-lista" }
const _hoisted_14 = { class: "indicador-nombre-wrap" }
const _hoisted_15 = { class: "indicador-nombre" }
const _hoisted_16 = { class: "instrumento-info" }
const _hoisted_17 = { class: "instrumento-nombre" }
const _hoisted_18 = ["disabled", "onClick"]
const _hoisted_19 = {
  key: 0,
  class: "modal-ficha-overlay"
}
const _hoisted_20 = { class: "modal-ficha-panel" }

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import FichaDocumental from '@/components/FichaDocumental.vue'
import FichaEstadistica from '@/components/FichaEstadistica.vue'

import { useGestionVersiones } from '@/store/GestionVersiones'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'
import { useAutenticar } from '@/store/IngresoAutenticar'
import { useComponentes } from '@/store/GestionComponentes'

import type Instrumento from '@/interfaces/Instrumetos'
import type Indicador from '@/interfaces/Indicador'
import type Componente from '@/interfaces/Componente'

interface RutaNodo { tipo: string; ordinal: string; nombre: string }

type TipoFiltroInfo = '' | 'documental' | 'cuantitativa' | 'opinion'

interface InstrumentoItem {
  idInstrumento: number | null
  // Un formulario de opinión es compartido por varios indicadores, así que el
  // instrumento por sí solo ya no identifica qué se va a aplicar.
  idIndicador: number | null
  instrumentoNombre: string
  tipoKey: Exclude<TipoFiltroInfo, ''> | null
  tipoLabel: string
  routeName: 'FichaDocumental' | 'FichaEstadistica' | 'FichaOpinion' | null
}

interface IndicadorItem {
  idIndicador: number
  nombre: string
  ordinal: number | null
  idTipoComponente: number
  instrumentos: InstrumentoItem[]
}

interface AspectoItem {
  idComponente: number
  nombre: string
  ordinal: string
  indicadores: IndicadorItem[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EvidenciasInstrumentosView',
  setup(__props) {

/**
 * Carga de evidencias por instrumento.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

const route = useRoute()
const router = useRouter()

const idVersion = computed(() => Number(route.params.id_version ?? NaN))

const versionesStore = useGestionVersiones()
const indicadoresStore = useGestionIndicadores()
const instrumentosStore = useGestionInstrumento()
const indicadorInstrumentoStore = useGestionIndicadorInstrumentos()
const tipoComponenteStore = useGestionTipoComponente()
const evaluadoresStore = useGestionIndicadorEvaluadores()
const authStore = useAutenticar()
const componentesStore = useComponentes()

const loading = ref(false)
const error = ref<string | null>(null)
const nombreModelo = ref('')

const modalFicha = ref<{
  tipo: 'documental' | 'estadistica'
  idInstrumento: number
  nombreVersion: string
  rutaComponente: RutaNodo[]
  nombreIndicador: string
} | null>(null)

const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value = tipo
  mensajeVisible.value = true
}

const arbol = ref<AspectoItem[]>([])

const filtroTipoInfo = ref<TipoFiltroInfo>('')

const opcionesFiltro = computed(() => ([
  { label: 'Todos', value: '' as TipoFiltroInfo },
  { label: 'Documental', value: 'documental' as TipoFiltroInfo },
  { label: 'Cuantitativa', value: 'cuantitativa' as TipoFiltroInfo },
  { label: 'Opinión', value: 'opinion' as TipoFiltroInfo }
]))

const arbolFiltrado = computed<AspectoItem[]>(() => {
  if (!filtroTipoInfo.value) return arbol.value
  return arbol.value
    .map(aspecto => ({
      ...aspecto,
      indicadores: aspecto.indicadores
        .map(ind => ({
          ...ind,
          instrumentos: ind.instrumentos.filter(i => i.tipoKey === filtroTipoInfo.value)
        }))
        .filter(ind => ind.instrumentos.length > 0)
    }))
    .filter(aspecto => aspecto.indicadores.length > 0)
})

function tipoInfo(idTipo: number): { key: InstrumentoItem['tipoKey']; label: string; routeName: InstrumentoItem['routeName'] } {
  const tipo = tipoComponenteStore.tipos.find(t => t.id_tipo_componente === idTipo)
  const raw = String((tipo?.nombre ?? tipo?.tipo ?? '')).trim()
  const nombre = raw.toLowerCase()

  if (nombre.includes('documental')) {
    return { key: 'documental', label: raw || 'Documental', routeName: 'FichaDocumental' }
  }
  if (nombre.includes('opinión') || nombre.includes('opinion')) {
    return { key: 'opinion', label: raw || 'Opinión', routeName: 'FichaOpinion' }
  }
  if (nombre.includes('cuantit') || nombre.includes('estad')) {
    return { key: 'cuantitativa', label: raw || 'Cuantitativa', routeName: 'FichaEstadistica' }
  }
  return { key: null, label: raw || String(idTipo), routeName: null }
}

function abrirFichaDirecta(inst: InstrumentoItem, aspecto?: AspectoItem, ind?: IndicadorItem) {
  if (!inst.idInstrumento) {
    mostrarMensaje('Este indicador no tiene instrumento asignado.', 'error')
    return
  }
  if (!inst.routeName) {
    mostrarMensaje('No se pudo determinar el tipo de ficha para este indicador.', 'error')
    return
  }
  if (inst.tipoKey === 'documental' || inst.tipoKey === 'cuantitativa') {
    modalFicha.value = {
      tipo: inst.tipoKey === 'documental' ? 'documental' : 'estadistica',
      idInstrumento: inst.idInstrumento!,
      nombreVersion: nombreModelo.value,
      rutaComponente: aspecto ? buildRutaComponente(aspecto.idComponente) : [],
      nombreIndicador: ind?.nombre ?? ''
    }
    return
  }
  // Se propaga el indicador: el formulario es compartido, y sin esto la ficha
  // no sabe cuál de los indicadores vinculados se está aplicando.
  const rawEjecucion = Array.isArray(route.query.id_ejecucion) ? route.query.id_ejecucion[0] : route.query.id_ejecucion
  const idEjecucion = Number(rawEjecucion)
  router.push({
    name: inst.routeName,
    query: {
      id_instrumento: inst.idInstrumento,
      id_version: idVersion.value,
      ...(inst.idIndicador ? { id_indicador: inst.idIndicador } : {}),
      ...(Number.isFinite(idEjecucion) && idEjecucion > 0 ? { id_ejecucion: idEjecucion } : {})
    }
  })
}

function volver() {
  router.push({ name: 'ProcesoAutoevaluacionUsuario' })
}

function formatOrdinal(ordinal: string): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

function buildRutaComponente(idComponente: number): RutaNodo[] {
  type NodoTemp = { idTipo: number | null | undefined; ordinal: string; nombre: string }
  const path: NodoTemp[] = []
  function buscar(nodos: Componente[], target: number, padres: NodoTemp[]): boolean {
    for (const n of nodos) {
      const nodoTemp: NodoTemp = { idTipo: n.id_tipo_componente, ordinal: formatOrdinal(n.ordinal ?? ''), nombre: n.nombre }
      if (n.id_componente === target) {
        padres.forEach(p => path.push(p))
        path.push(nodoTemp)
        return true
      }
      if (n.children?.length && buscar(n.children, target, [...padres, nodoTemp])) return true
    }
    return false
  }
  const raiz = componentesStore.componentes[0]
  if (raiz?.children) buscar(raiz.children, idComponente, [])
  return path.map(p => ({
    tipo: tipoComponenteStore.tipos.find(t => t.id_tipo_componente === p.idTipo)?.nombre ?? '',
    ordinal: p.ordinal,
    nombre: p.nombre
  }))
}

function flattenComponentes(lista: Componente[], mapa: Map<number, { nombre: string; ordinal: string }>) {
  for (const c of lista) {
    if (c.id_componente) {
      mapa.set(c.id_componente, { nombre: c.nombre, ordinal: c.ordinal ?? '' })
    }
    if (c.children?.length) {
      flattenComponentes(c.children, mapa)
    }
  }
}

async function cargarInstrumentosDeVersion() {
  if (!Number.isFinite(idVersion.value) || idVersion.value <= 0) {
    error.value = 'ID de versión inválido.'
    return
  }

  loading.value = true
  error.value = null

  try {
    arbol.value = []

    const idUsuario = authStore.usuarioActual?.id

    // Todas las cargas independientes en paralelo: solo la cadena
    // cargarRaizPorModelo → cargarArbolDesdeRaiz necesita esperar a version.
    const [, version] = await Promise.all([
      tipoComponenteStore.getTipos(),
      versionesStore.getVersionById(idVersion.value),
      indicadoresStore.cargarTodos(),
      instrumentosStore.cargarTodos(),
      indicadorInstrumentoStore.getRelaciones(),
      idUsuario
        ? evaluadoresStore.cargar({ id_version: idVersion.value, id_usuario: idUsuario })
        : Promise.resolve()
    ])
    nombreModelo.value = version?.nombre ?? ''

    // Árbol de componentes: secuencial porque cada paso depende del anterior
    const idModelo = (version as { id_modelo?: number } | null)?.id_modelo ?? null
    if (idModelo) {
      const raiz = await componentesStore.cargarRaizPorModelo(idModelo)
      if (raiz?.id_componente) {
        await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)
      }
    }

    const componenteMap = new Map<number, { nombre: string; ordinal: string }>()
    flattenComponentes(componentesStore.componentes, componenteMap)

    // Filtrar indicadores de la versión según permisos
    let indicadoresVersion: Indicador[]

    const idsEvidencia = new Set(
      evaluadoresStore.asignaciones
        .filter(a => (a.tarea === 'evidencias' || a.tarea === 'instrumentos') && a.id_indicador != null)
        .map(a => a.id_indicador as number)
    )

    if (idsEvidencia.size > 0) {
      indicadoresVersion = indicadoresStore.indicadores.filter(
        i => i.id_version === idVersion.value && idsEvidencia.has(i.id_indicador)
      )
    } else if (authStore.esAdmin) {
      indicadoresVersion = indicadoresStore.indicadores.filter(i => i.id_version === idVersion.value)
    } else {
      const idsAsignados = new Set(
        evaluadoresStore.asignaciones
          .map(a => a.id_indicador)
          .filter((id): id is number => id != null)
      )
      if (idsAsignados.size === 0) { arbol.value = []; return }
      indicadoresVersion = indicadoresStore.indicadores.filter(
        i => i.id_version === idVersion.value && idsAsignados.has(i.id_indicador)
      )
    }

    const idsIndicadoresVersion = new Set(indicadoresVersion.map(i => i.id_indicador))
    const instrumentosPorIndicador = new Map<number, number[]>()
    for (const rel of indicadorInstrumentoStore.relaciones) {
      if (!idsIndicadoresVersion.has(rel.id_indicador)) continue
      const lista = instrumentosPorIndicador.get(rel.id_indicador) ?? []
      if (!lista.includes(rel.id_instrumento)) lista.push(rel.id_instrumento)
      instrumentosPorIndicador.set(rel.id_indicador, lista)
    }

    // Agrupar indicadores por componente
    const porComponente = new Map<number, IndicadorItem[]>()
    for (const ind of indicadoresVersion) {
      const idsInst = instrumentosPorIndicador.get(ind.id_indicador) ?? []
      const info = tipoInfo(ind.id_tipo_componente)

      const instrumentos: InstrumentoItem[] = idsInst.length === 0
        ? [{
            idInstrumento: null,
            idIndicador: ind.id_indicador,
            instrumentoNombre: 'Sin instrumento asignado',
            tipoKey: info.key,
            tipoLabel: info.label,
            routeName: info.routeName
          }]
        : idsInst.map(idInst => {
            const inst = instrumentosStore.instrumentos.find(i => i.id_instrumento === idInst) as Instrumento | undefined
            return {
              idInstrumento: idInst,
              idIndicador: ind.id_indicador,
              // Se prefiere la descripción: ahí va el nombre del formulario
              // ("Encuesta estudiantes"). `herramienta` es solo el tipo
              // ("encuesta") y no distingue un formulario de otro.
              instrumentoNombre: (inst as any)?.descripcion || inst?.herramienta || String(idInst),
              tipoKey: info.key,
              tipoLabel: info.label,
              routeName: info.routeName
            }
          })

      const idComp = ind.id_componente
      if (!porComponente.has(idComp)) porComponente.set(idComp, [])
      porComponente.get(idComp)!.push({
        idIndicador: ind.id_indicador,
        nombre: ind.nombre ?? ind.enunciado,
        ordinal: ind.ordinal ?? null,
        idTipoComponente: ind.id_tipo_componente,
        instrumentos
      })
    }

    // Ordenar indicadores dentro de cada componente por su ordinal
    for (const inds of porComponente.values()) {
      inds.sort((a, b) => (a.ordinal ?? 9999) - (b.ordinal ?? 9999))
    }

    // Construir y ordenar aspectos por ordinal numérico
    const nuevaArbol: AspectoItem[] = []
    for (const [idComp, inds] of porComponente) {
      const compInfo = componenteMap.get(idComp)
      nuevaArbol.push({
        idComponente: idComp,
        nombre: compInfo?.nombre ?? `Componente ${idComp}`,
        ordinal: compInfo?.ordinal ?? '',
        indicadores: inds
      })
    }

    nuevaArbol.sort((a, b) =>
      (a.ordinal).localeCompare(b.ordinal, undefined, { numeric: true })
    )

    arbol.value = nuevaArbol
  } catch (e: any) {
    error.value = e?.message || 'Error al cargar instrumentos.'
  } finally {
    loading.value = false
  }
}

onMounted(cargarInstrumentosDeVersion)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(LoadingOverlay, {
        visible: loading.value,
        message: "Cargando instrumentos..."
      }, null, 8, ["visible"]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _cache[3] || (_cache[3] = _createElementVNode("h1", null, "Subir evidencias", -1)),
          (nombreModelo.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(nombreModelo.value), 1))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", { class: "botones" }, [
          _createElementVNode("button", {
            class: "btn salir",
            onClick: volver
          }, "Volver")
        ])
      ]),
      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "divider" }, null, -1)),
      (!loading.value && error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(error.value), 1))
        : (!loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(opcionesFiltro.value, (t) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: t.label,
                    class: _normalizeClass(['btn-filtro', filtroTipoInfo.value === t.value ? 'activo' : '']),
                    onClick: ($event: any) => (filtroTipoInfo.value = t.value)
                  }, _toDisplayString(t.label), 11, _hoisted_7))
                }), 128))
              ]),
              (arbolFiltrado.value.length === 0)
                ? (_openBlock(), _createElementBlock("p", _hoisted_8, " No se encontraron instrumentos asociados a esta versión. "))
                : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(arbolFiltrado.value, (aspecto) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: aspecto.idComponente,
                        class: "aspecto-seccion"
                      }, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("span", _hoisted_11, _toDisplayString(formatOrdinal(aspecto.ordinal)), 1),
                          _createElementVNode("span", _hoisted_12, _toDisplayString(aspecto.nombre), 1)
                        ]),
                        _createElementVNode("div", _hoisted_13, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(aspecto.indicadores, (ind) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: ind.idIndicador,
                              class: "indicador-bloque"
                            }, [
                              _createElementVNode("div", _hoisted_14, [
                                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "indicador-badge" }, "Indicador", -1)),
                                _createElementVNode("span", _hoisted_15, _toDisplayString(ind.nombre), 1)
                              ]),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ind.instrumentos, (inst, idx) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: idx,
                                  class: "instrumento-card"
                                }, [
                                  _createElementVNode("div", _hoisted_16, [
                                    (inst.tipoLabel)
                                      ? (_openBlock(), _createElementBlock("span", {
                                          key: 0,
                                          class: _normalizeClass(["instrumento-tipo-badge", `tipo-${inst.tipoKey}`])
                                        }, _toDisplayString(inst.tipoLabel), 3))
                                      : _createCommentVNode("", true),
                                    _createElementVNode("span", _hoisted_17, _toDisplayString(inst.instrumentoNombre), 1)
                                  ]),
                                  _createElementVNode("button", {
                                    class: "btn guardar",
                                    disabled: !inst.idInstrumento || !inst.routeName,
                                    onClick: ($event: any) => (abrirFichaDirecta(inst, aspecto, ind))
                                  }, _toDisplayString(inst.tipoKey === 'opinion' ? 'Aplicar Instrumento' : 'Cargar ficha'), 9, _hoisted_18)
                                ]))
                              }), 128))
                            ]))
                          }), 128))
                        ])
                      ]))
                    }), 128))
                  ]))
            ]))
          : _createCommentVNode("", true),
      _createVNode(MensajeFlotante, {
        visible: mensajeVisible.value,
        mensaje: mensajeTexto.value,
        tipo: mensajeTipo.value,
        onCerrar: _cache[0] || (_cache[0] = ($event: any) => (mensajeVisible.value = false))
      }, null, 8, ["visible", "mensaje", "tipo"])
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (modalFicha.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
            _createElementVNode("div", _hoisted_20, [
              (modalFicha.value.tipo === 'documental')
                ? (_openBlock(), _createBlock(FichaDocumental, {
                    key: 0,
                    idInstrumento: modalFicha.value.idInstrumento,
                    nombreVersion: modalFicha.value.nombreVersion,
                    rutaComponente: modalFicha.value.rutaComponente,
                    nombreIndicador: modalFicha.value.nombreIndicador,
                    onCerrar: _cache[1] || (_cache[1] = ($event: any) => (modalFicha.value = null))
                  }, null, 8, ["idInstrumento", "nombreVersion", "rutaComponente", "nombreIndicador"]))
                : _createCommentVNode("", true),
              (modalFicha.value.tipo === 'estadistica')
                ? (_openBlock(), _createBlock(FichaEstadistica, {
                    key: 1,
                    idInstrumento: modalFicha.value.idInstrumento,
                    nombreVersion: modalFicha.value.nombreVersion,
                    rutaComponente: modalFicha.value.rutaComponente,
                    nombreIndicador: modalFicha.value.nombreIndicador,
                    onCerrar: _cache[2] || (_cache[2] = ($event: any) => (modalFicha.value = null))
                  }, null, 8, ["idInstrumento", "nombreVersion", "rutaComponente", "nombreIndicador"]))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})